import { ReactNode, useState, useCallback } from 'react';
import { AppShell } from '@mantine/core';
import { useClickOutside, useMediaQuery } from '@mantine/hooks';
import { DomainKey } from '../config/domains';
import MainHeader from './components/MainHeader';
import MainSidebar from './components/MainSidebar';
import MainFooter from './components/MainFooter';
import { AppUpdateWatcher } from '@/domain/appUpdate';
import MainBackground from './components/MainBackground';
import ScreenLockModal from './components/ScreenLockModal';
import { LayoutContainer } from '../components/ui/layout';
import { usePreferencesStore } from '../stores';
import { usePresenceManager } from '../hooks/usePresenceManager';
import { PresenceStatus } from '@actschurch/ccms-sdk';
import { BadgeSubscribers } from '../domain/badges/BadgeSubscribers';
import { ProfileCompletionBanner } from '../components/members/ProfileCompletionBanner';

interface MainLayoutProps {
  children: ReactNode;
  showSidebar?: boolean;
  showHeader?: boolean;
  showFooter?: boolean;
  domainKey?: DomainKey;
  /** Force full-width layout regardless of user preference (for special pages like Board view) */
  forceFullWidth?: boolean;
}

/**
 * MainLayout Component
 * Main layout orchestrator for the application interface
 * Provides header, sidebar, footer, and content area with screen lock functionality
 */
export default function MainLayout({
  children,
  showSidebar = true,
  showHeader = true,
  showFooter = true,
  domainKey,
  forceFullWidth = false,
}: MainLayoutProps) {
  // Mobile sidebar state (not persisted - resets on refresh)
  const [mobileSidebarOpened, setMobileSidebarOpened] = useState(false);

  // Check if we're on mobile (matches AppShell breakpoint 'sm')
  // Use getInitialValueInEffect: false to get synchronous initial value based on window size
  const isMobile = useMediaQuery('(max-width: 48em)', undefined, {
    getInitialValueInEffect: false,
  });

  const sidebarCollapsed = usePreferencesStore((state) => state.sidebarCollapsed);
  const toggleSidebar = usePreferencesStore((state) => state.toggleSidebar);
  const setSidebarCollapsed = usePreferencesStore((state) => state.setSidebarCollapsed);

  // Menu UI preferences
  const menuIconOnly = usePreferencesStore((state) => state.menuIconOnly);
  const menuAutoHide = usePreferencesStore((state) => state.menuAutoHide);

  // Handle sidebar toggle - different behavior for mobile vs desktop
  // Use isMobile !== false to handle undefined initial state safely
  const handleSidebarToggle = useCallback(() => {
    if (isMobile !== false) {
      // Mobile or undefined (SSR) - toggle mobile sidebar
      setMobileSidebarOpened((prev) => !prev);
    } else {
      // Desktop - toggle persisted sidebar collapse
      toggleSidebar();
    }
  }, [isMobile, toggleSidebar]);

  // Screen lock state - persisted in preferences
  const isLocked = usePreferencesStore((state) => state.screenLocked);
  const setScreenLocked = usePreferencesStore((state) => state.setScreenLocked);
  const setPresenceStatus = usePreferencesStore((state) => state.setPresenceStatus);

  // Presence integration
  const { isConnected, currentUserPresence, updateStatus } = usePresenceManager();

  // Auto-close sidebar when clicking outside
  // On mobile: always close when clicking outside
  // On desktop: only close if auto-hide is enabled
  const sidebarRef = useClickOutside(() => {
    if (isMobile && mobileSidebarOpened) {
      setMobileSidebarOpened(false);
    } else if (!isMobile && !sidebarCollapsed && menuAutoHide) {
      setSidebarCollapsed(true);
    }
  });

  const handleLockScreen = async () => {
    // Lock the screen
    setScreenLocked(true);

    // Set presence to Away when locking
    try {
      setPresenceStatus('Away' as unknown as PresenceStatus);

      // Check connection state before attempting to update
      console.log('[MainLayout] Presence state:', {
        isConnected,
        currentUserPresence,
      });

      if (isConnected) {
        console.log('[MainLayout] Attempting to update presence to Away...');
        await updateStatus('Away' as unknown as PresenceStatus);
        console.log('[MainLayout] Presence successfully set to Away (screen locked)');
      } else {
        console.warn('[MainLayout] Presence hub not connected, status saved locally only');
      }
    } catch (error: any) {
      console.error('[MainLayout] Failed to update presence on lock:', {
        message: error?.message,
        name: error?.name,
        cause: error?.cause,
        fullError: error,
      });
      // Continue anyway - the local preference is saved
    }
  };

  const handleUnlockScreen = async () => {
    // Unlock the screen
    setScreenLocked(false);

    // Set presence back to Online when unlocking
    try {
      setPresenceStatus('Online' as unknown as PresenceStatus);

      // Check connection state before attempting to update
      console.log('[MainLayout] Presence state:', {
        isConnected: isConnected,
        currentUserPresence,
        error: null, // Replace with actual error handling if needed
      });

      if (isConnected) {
        console.log('[MainLayout] Attempting to update presence to Online...');
        await updateStatus('Online' as unknown as PresenceStatus);
        console.log('[MainLayout] Presence successfully set to Online (screen unlocked)');
      } else {
        console.warn('[MainLayout] Presence hub not connected, status saved locally only');
      }
    } catch (error: any) {
      console.error('[MainLayout] Failed to update presence on unlock:', {
        message: error?.message,
        name: error?.name,
        cause: error?.cause,
        fullError: error,
      });
      // Continue anyway - the local preference is saved
    }
  };

  return (
    <>
      {/* Screen Lock Modal - Render first for proper z-index */}
      <ScreenLockModal opened={isLocked} onUnlock={handleUnlockScreen} />

      {/* Live count badges — each subscriber self-gates on role and renders
          nothing visible. Mounted here so badges keep ticking across every
          domain (sidebar items + main-page tiles). */}
      <BadgeSubscribers />

      <MainBackground>
        <AppShell
          header={showHeader ? { height: 60 } : undefined}
          navbar={
            showSidebar
              ? {
                  width: menuIconOnly ? { sm: 100, lg: 110 } : { sm: 230, lg: 280 },
                  breakpoint: 'sm',
                  collapsed: { mobile: !mobileSidebarOpened, desktop: sidebarCollapsed },
                }
              : undefined
          }
          footer={showFooter ? { height: 60 } : undefined}
          padding="md"
        >
          {/* Header */}
          {showHeader && (
            <AppShell.Header>
              <MainHeader
                onSidebarToggle={handleSidebarToggle}
                sidebarCollapsed={isMobile ? !mobileSidebarOpened : sidebarCollapsed}
                onLockScreen={handleLockScreen}
                showBurger={showSidebar}
              />
            </AppShell.Header>
          )}

          {/* Sidebar */}
          {showSidebar && (
            <AppShell.Navbar ref={sidebarRef}>
              <MainSidebar domainKey={domainKey} collapsed={sidebarCollapsed} />
            </AppShell.Navbar>
          )}

          {/* Main Content */}
          <AppShell.Main>
            <LayoutContainer forceFullWidth={forceFullWidth} py="md">
              {/* Dismissible "Complete your profile" nudge (never a gate). */}
              <ProfileCompletionBanner />
              {children}
            </LayoutContainer>
          </AppShell.Main>

          {/* Footer */}
          {showFooter && (
            <AppShell.Footer>
              <MainFooter />
            </AppShell.Footer>
          )}

          {/*
            Polls the published manifest and renders nothing itself. Kept
            outside the footer so hiding the footer cannot switch off update
            checking, which a required update depends on.
          */}
          <AppUpdateWatcher />
        </AppShell>
      </MainBackground>
    </>
  );
}
